/*
 * $Id:: routes.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React from 'react';

import { useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';

import { setLocalStorage } from '../common/utils';
import AccountVerificationPage from '../pages/AuthenticatedPage/AccountVerificationPage';
import UsernameChangeVerificationPage from '../pages/AuthenticatedPage/UsernameChangeVerificationPage';
import { me as meSlice } from '../pages/AuthenticatedPage/authenticationSlice';
import ForgottenLoginPage from '../pages/AuthenticatedPage/ForgottenLoginPage';
import LoginRegisterPage from '../pages/AuthenticatedPage/LoginRegisterPage';
import ResetPasswordPage from '../pages/AuthenticatedPage/ResetPasswordPage';
import CorrespondenceDetails from '../pages/CorrespondenceDetails';
import CorrespondencePage from '../pages/CorrespondencePage';
import HomePage from '../pages/HomePage';
import CreateChildAccount from '../pages/MyAccountPage/CreateChildAccount';
import EditChildAccount from '../pages/MyAccountPage/EditChildAccount';
import MyAccountPage from '../pages/MyAccountPage/MyAccount';
import MyApplicationsPage from '../pages/MyApplicationsPage';
import MyDraftsPage from '../pages/MyDraftsPage';
import NotFoundPage from '../pages/NotFoundPage';
import OnlineServicesPage from '../pages/OnlineServicesPage';
import EServicesPage from '../pages/OnlineServicesPage/components/EServicesPage';
import PaymentsPage from '../pages/PaymentsPage';
import SignaturesPage from '../pages/SignaturesPage';
import SSOLoginPage from '../pages/SSOPage/LoginPage';
import guards from './guards';

export const routes = [
    {
        path: '/',
        exact: true,
        component: HomePage,
        authGuard: true,
        getUpdateUserInfo: true,
    },
    {
        path: ['/login', '/new-account'],
        exact: true,
        component: LoginRegisterPage,
    },
    {
        path: '/forgotten-login',
        exact: true,
        component: ForgottenLoginPage,
    },
    {
        path: '/login/oauth2/challenge',
        exact: true,
        component: SSOLoginPage,
    },
    {
        path: '/reset/token/:token',
        exact: true,
        component: ResetPasswordPage,
    },
    {
        path: ['/child/registration/token/:token', '/registration/token/:token', '/registration/verify'],
        exact: true,
        component: AccountVerificationPage,
    },
    {
        path: ['/change-username/token/:token'],
        exact: true,
        component: UsernameChangeVerificationPage,
    },
    {
        path: '/my-account/:tab',
        exact: true,
        authGuard: true,
        component: MyAccountPage,
    },
    {
        path: '/child-accounts/create',
        exact: true,
        authGuard: true,
        component: CreateChildAccount,
        canAccess: guards.canAccessCreateOrEditChildAccount,
    },
    {
        path: '/child-accounts/edit',
        exact: true,
        authGuard: true,
        component: EditChildAccount,
        canAccess: guards.canAccessCreateOrEditChildAccount,
    },
    {
        path: '/my-applications',
        exact: true,
        authGuard: true,
        component: MyApplicationsPage,
        canAccess: guards.canAccessTrademarksOrDesigns,
    },
    {
        path: '/my-drafts',
        exact: true,
        authGuard: true,
        component: MyDraftsPage,
        canAccess: guards.canAccessTrademarksOrDesigns,
    },
    {
        path: '/online-services',
        exact: true,
        authGuard: true,
        component: OnlineServicesPage,
        canAccess: guards.canAccessTrademarksOrDesigns,
        getUpdateUserInfo: true,
    },
    {
        path: '/online-services/:service',
        exact: true,
        authGuard: true,
        component: OnlineServicesPage,
        canAccess: guards.canAccessTrademarksOrDesigns,
        getUpdateUserInfo: true,
    },
    {
        path: '/online-services/:service/e-services',
        exact: true,
        authGuard: true,
        component: EServicesPage,
        canAccess: guards.canAccessTrademarksOrDesigns,
        getUpdateUserInfo: true,
    },
    {
        path: '/correspondence',
        exact: true,
        authGuard: true,
        component: CorrespondencePage,
        canAccess: guards.canAccessTrademarksOrDesigns,
    },
    {
        path: '/payments/:tab',
        exact: true,
        authGuard: true,
        component: PaymentsPage,
        canAccess: guards.canAccessTrademarksOrDesigns,
    },
    {
        path: '/signatures',
        exact: true,
        authGuard: true,
        component: SignaturesPage,
        canAccess: guards.canAccessTrademarksOrDesigns,
    },
    {
        path: '/correspondence/:id',
        exact: true,
        authGuard: true,
        component: CorrespondenceDetails,
        canAccess: guards.canAccessTrademarksOrDesigns,
    },
    {
        path: '*',
        component: NotFoundPage,
    },
];

const RouteWithSubRoutes = ({ component: Component, authGuard, canAccess, getUpdateUserInfo, ...rest }) => {
    const me = useSelector(meSlice);

    const isLoggedIn = !!me;
    const { userType, authorities } = me || {};

    const allowedComponent = guards.accessCheckRender(Component, userType, authorities, canAccess);

    const renderComponent = (props) => {
        setLocalStorage([{ item: 'showExpiringMessages', value: false }]);
        return <Component {...props} />;
    };

    const renderFunction = authGuard ? guards.AuthenticatedRender(allowedComponent, isLoggedIn, getUpdateUserInfo) : renderComponent;

    return <Route render={renderFunction} {...rest} />;
};

export const RenderRoutes = () => (
    <Switch>
        {routes.map((route, i) => (
            <RouteWithSubRoutes key={i} {...route} />
        ))}
    </Switch>
);
